import {DAY, WEEK} from '@edume/bento/contentAssignment';

import {dueDateRulesAreDifferent} from '../utils/dueDateUtils';
import type {TeamWithDueDate} from '../utils/dueDateUtils';

export type UnitType = typeof DAY | typeof WEEK;

const DEFAULT_UNIT: UnitType = DAY;
const DEFAULT_COUNT = 30;

function createLimits(minValue: number, maxValue: number) {
  return {
    minValue,
    maxValue,
    maxInputLength: maxValue.toString().length,
  };
}

const DUE_DATE_CONFIG = {
  [DAY]: {
    ...createLimits(1, 365), // 365 days - Up to a year
    localizationKey: 'MandatorySlidePanel.dueDate.days',
  },
  [WEEK]: {
    ...createLimits(1, 52), // 52 weeks - Up to a year
    localizationKey: 'MandatorySlidePanel.dueDate.weeks',
  },
};

// Helper to get max - min limits by unitType
export const getConfigForUnitType = (unit: UnitType) => DUE_DATE_CONFIG[unit];

// Create selector options for MandatoryContentSlidePanel
export function createSelectorOptions(
  formatMessage: (key: string, variables?: Record<string, any>) => string,
  onUnitChange: (unit: UnitType, itemIndex: number) => void
) {
  function getDataAutomationString(unit: UnitType) {
    if (unit === DAY) {
      return 'day-option';
    }
    if (unit === WEEK) {
      return 'week-option';
    }
    throw new Error(`Unknown unit: ${unit}`);
  }

  return Object.entries(DUE_DATE_CONFIG).map(
    ([unit, config], itemIndex: number) => ({
      dataAutomation: getDataAutomationString(unit as UnitType),
      title: formatMessage(config.localizationKey, {
        count: 2,
      }),
      value: unit,
      callback: () => onUnitChange(unit as UnitType, itemIndex),
    })
  );
}

// If existing due date rule days are set, return the first one found, converted to count and unit.
// If not, return default values.
export const getInitialDueDateToDisplay = <T>(
  mandatoryTeams: Array<
    T & Partial<{dueDateRuleValue: number; dueDateDisplayUnit: string}>
  >
): {count: number; unit: UnitType} => {
  if (mandatoryTeams.length === 0) {
    return {count: DEFAULT_COUNT, unit: DEFAULT_UNIT};
  }

  const existingDueDate = mandatoryTeams[0];
  const count = existingDueDate.dueDateRuleValue ?? DEFAULT_COUNT;
  const unit = (existingDueDate.dueDateDisplayUnit as UnitType) ?? DEFAULT_UNIT;
  return {count, unit};
};

export function getDueDateInDays(dueDateNumber: number, dueDateUnit: UnitType) {
  if (dueDateUnit === WEEK) {
    return dueDateNumber * 7;
  }
  return dueDateNumber;
}

// Calculate if any changes have been made to either the team list, or due date
export const getEditedFields = (
  existingMandatoryTeams: Array<
    {
      id: number;
      name: string;
      isSmartTeam: boolean;
    } & TeamWithDueDate
  >,
  selectedTeamIds: number[],
  dueDateNumber: number,
  dueDateUnit: UnitType
) => {
  if (!existingMandatoryTeams.length) {
    return [];
  }

  const existingTeamIds = existingMandatoryTeams
    .map((team) => team.id)
    .sort((a, b) => a - b);
  const selectedIdsSorted = [...selectedTeamIds].sort((a, b) => a - b);
  const haveTeamsChanged =
    existingTeamIds.length !== selectedIdsSorted.length ||
    existingTeamIds.some((id, idx) => id !== selectedIdsSorted[idx]);

  const propAsDueDate = {
    dueDateDisplayUnit: dueDateUnit,
    dueDateRuleValue: dueDateNumber,
  };

  const hasDueDateChanged = dueDateRulesAreDifferent(
    propAsDueDate,
    existingMandatoryTeams[0]
  );

  const editedFields: string[] = [];
  if (haveTeamsChanged) {
    editedFields.push('teams');
  }
  if (hasDueDateChanged) {
    editedFields.push('due_date');
  }
  return editedFields;
};
